import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useThemeContext } from '../../../../theme/ThemeContext'
import { Filters, FiltersBar, FilterSelector, FilterTextField, searchToFilters } from '../../../Utils/Filters/Filter'
import { fetchGoals } from '../../../../actions/goal/goalList'
import Goal from '../../../../models/Goal'
import { GoalListState } from '../../../../reducers/goal/goalList'
import { AppState } from '../../../../reducers'
import Goals from '..'
import { Button, Fab, Stack, Tooltip } from '@mui/material'
import { Add, FileUpload } from '@mui/icons-material'
import { personTypeToDescription } from '../../../Client/Profile'
import { SessionState } from '../../../../reducers/auth/session'
import { roleToURLPrefix } from '../../../../App'
import { PERSON } from '../../../../models/User'
import { t } from 'i18next'
import { FilterFromDate, FilterToDate } from '../../../Utils/Filters'
import moment from 'moment'
import { XlsImportGoalDialog } from '../GoalImportDialog';
import { upsertBulkGoal } from '../../../../actions/goal/goalUpsert';
import { ProductBrandListState } from '../../../../reducers/product/productBrandList';
import { fetchAllProductBrands } from '../../../../actions/product/productBrandList';

interface Props {
  goalList: GoalListState
  productBrand: ProductBrandListState
  session: SessionState
}

export const translateStatusGoalDescription = (value: string): string => {
  const statusMap: { [key: string]: string } = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    CANCELLED: 'cancelled'
  }
  return t(`goal:${statusMap[value]}` || value)
}

export const translateTypeOfGoalDescription = (value: string): string => {
  const statusMap: { [key: string]: string } = {
    $: 'currency',
    Qty: 'qty'
  }
  return t(`goal:${statusMap[value]}` || value)
}

export const GOAL_KIND_STANDARD =
  'STANDARD'

export const GOAL_KIND_CONSTANT_AVERAGE =
  'CONSTANT_AVERAGE'

export const translateGoalKindDescription = (
  value: string
): string => {
  const descriptions: {
    [key: string]: string
  } = {
    STANDARD:
      'Estándar',

    CONSTANT_AVERAGE:
      'Por promedio'
  }

  return descriptions[value] ?? value
}

export const GoalList: React.FC<Props> = ({ goalList, productBrand, session }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation(['goal', 'person'])
  const { theme } = useThemeContext()
  const [showXlsImportGoalDialog, setShowXlsImportGoalDialog] = useState<boolean>(false)

  const currentFilters =
    searchToFilters(history.location.search)

  const selectedGoalKind =
    currentFilters.goalKind ??
    GOAL_KIND_STANDARD

  const isConstantAverageFilter =
    selectedGoalKind ===
    GOAL_KIND_CONSTANT_AVERAGE

  useEffect(() => {
    const params =
      new URLSearchParams(
        history.location.search
      )

    const goalKind =
      params.get('goalKind')

    const personType =
      params.get('personType')

    if (
      goalKind ===
      GOAL_KIND_CONSTANT_AVERAGE &&
      personType !== 'C'
    ) {
      params.set(
        'personType',
        'C'
      )

      history.replace(
        `?${params.toString()}`
      )
    }
  }, [
    history,
    history.location.search
  ])

  useEffect(() => {
    dispatch(fetchAllProductBrands())
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [])

  useEffect(() => {
    dispatch(fetchGoals(history.location.search))
  }, [dispatch, history.location.search])

  const onClickEdit = (goal: Goal) => {
    history.push(`/${session.roles && roleToURLPrefix(session.roles.filter(role => role !== PERSON)[0])}/goals/update/${goal.id}`)
  }

  const handleBulkGoalImport = async (importedGoals: Goal[]) => {
    const handleResponse = () => {
      dispatch(fetchGoals(history.location.search))
      setShowXlsImportGoalDialog(false)
    }
    if (importedGoals.length > 0) dispatch(upsertBulkGoal(importedGoals, () => { handleResponse() }))
  }

  const initDefaultFilters: Filters = {
    from: moment().startOf('year').subtract(1, 'years').tz("America/Argentina/Buenos_Aires").format(),
    to: moment().endOf('month').tz("America/Argentina/Buenos_Aires").format(),
    goalKind: GOAL_KIND_STANDARD
  }

  const translatePersonTypeDescription = (value: string): string => t(personTypeToDescription(value), { ns: 'person' })

  return (
    <div>
      <FiltersBar defaultFilters={initDefaultFilters}>
        <FilterFromDate name="from" label={t("from")} />
        <FilterToDate name="to" label={t("to")} />
        <FilterSelector name="personType" label={t("personType")} style={{ minWidth: '300px' }} menuItems={['C', 'V']} textAdapter={translatePersonTypeDescription} />
        <FilterSelector name="goalKind" label="Tipo de objetivo" style={{ minWidth: '250px' }} menuItems={[GOAL_KIND_STANDARD, GOAL_KIND_CONSTANT_AVERAGE]} textAdapter={translateGoalKindDescription} />
        <FilterTextField name="byPersonTypeId" label={t("byPersonTypeId")} style={{ minWidth: '300px' }} />
        <FilterSelector name="status" label={t("status")} style={{ minWidth: '300px' }} menuItems={['ACTIVE', 'INACTIVE', 'CANCELLED']} textAdapter={translateStatusGoalDescription} />
      </FiltersBar>
      <Goals
        rows={(goalList.goals || []).map(s => ({ data: s }))}
        loading={goalList.isFetching}
        pagination={goalList.pagination}
        onClickInfo={onClickEdit}
      />
      <Stack display="flex" flexDirection='row' justifyContent="flex-start" padding="10px" paddingTop="20px">
        <Tooltip
          title={
            isConstantAverageFilter
              ? 'Los objetivos por promedio se generan desde su proceso específico.'
              : ''
          }
        >
          <span>
            <Button
              style={{ textTransform: 'none' }}
              onClick={() => setShowXlsImportGoalDialog(true)} variant="contained" color="primary" startIcon={<FileUpload />} disabled={isConstantAverageFilter}>
              {t('import')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <XlsImportGoalDialog isOpen={showXlsImportGoalDialog && !isConstantAverageFilter}
        brands={productBrand.productBrands || []}
        onConfirm={(bulkData) => { handleBulkGoalImport(bulkData) }}
        onCancel={() => setShowXlsImportGoalDialog(false)}
      />

      <Tooltip
        title={isConstantAverageFilter ? 'Los objetivos por promedio no se crean manualmente.' : t('create')}>
        <span>
          <Fab color="primary" variant="extended"
            sx={{ position: 'fixed', bottom: theme.spacing(4), right: theme.spacing(3) }}
            disabled={isConstantAverageFilter}
            onClick={() => history.push(`/${session.roles && roleToURLPrefix(session.roles.filter(role => role !== PERSON)[0])}/goals/create`)}>
            <Add />
          </Fab>
        </span>
      </Tooltip>
    </div>
  )
}

const mapStateToProps = (state: AppState) => ({
  goalList: state.goalList,
  productBrand: state.productBrandList,
  session: state.session
})

export default connect(mapStateToProps, null)(GoalList)